import { updateKeyInStore } from 'App';
import { store } from 'app/redux/store';
import { states } from 'states';
import { Validators } from 'utils/Validators';
import * as Yup from 'yup';
import { requiredFileIf, requiredFiles } from '../../useValidateQuestionnaire';
import {
  GeneralInfoStateValues,
  updateFieldArrayKey,
  updateKey,
} from './slice';

export const fields: IBaseField<keyof GeneralInfoStateValues | any>[] = [
  {
    name: 'businessName',
    label: 'Business Name',
    type: 'text',
    effect: updateKeyInStore(updateKey),
    tooltip: '',
    comment: true,
  },
  {
    name: 'employerIdentificationNumber',
    label: 'Employer Identification Number',
    type: 'maskedInput',
    mask: '99-9999999',
    effect: updateKeyInStore(updateKey),
    tooltip: '',
    comment: true,
  },
  {
    name: 'businessAddress',
    label: 'Business Address',
    type: 'text',
    effect: updateKeyInStore(updateKey),
    tooltip: '',
    comment: true,
  },
  {
    name: 'city',
    label: 'City',
    type: 'text',
    effect: updateKeyInStore(updateKey),
    tooltip: '',
    comment: true,
  },

  {
    name: 'zipCode',
    label: 'Zip Code',
    type: 'text',
    effect: updateKeyInStore(updateKey),
    tooltip: '',
    comment: true,
  },
  {
    name: 'state',
    label: 'State',
    type: 'state',
    effect: updateKeyInStore(updateKey),
    tooltip: '',
    comment: true,
  },
  {
    name: 'dateBusinessWasFormed',
    label: 'Date business was formed',
    type: 'date',
    effect: updateKeyInStore(updateKey),
    tooltip: '',
    comment: true,
  },
  {
    name: 'thisEntityIsOwnedByAn',
    label: 'This entity is owned by a(n)',
    type: 'select',
    options: [
      {
        label: 'Individual',
        value: 'Individual',
      },
      {
        label: 'Estate',
        value: 'Estate',
      },
      {
        label: 'Trust',
        value: 'Trust',
      },
      {
        label: 'Exempt Organization',
        value: 'Exempt Organization',
      },
      {
        label: 'Corporation',
        value: 'Corporation',
      },
    ],
    effect: updateKeyInStore(updateKey),
    tooltip: '',
    comment: true,
  },
  {
    name: 'inWhatStateDidYouRegisterYourBusiness',
    label: 'In what state did you register your business?',
    type: 'state',
    effect: updateKeyInStore(updateKey),
    tooltip: '',
    comment: true,
    dependencies: [
      {
        name: 'californiaSosNumber',
        label: 'California SOS Number',
        type: 'number',
        effect: updateKeyInStore(updateKey),

        comment: true,
        predicate: (v) => v === 'CA',
      },
      {
        name: 'didYouMakeYourYearCaliforniaFranchiseTaxPayment',
        label: 'Did you make your {year} California Franchise Tax Payment',
        type: 'radio',
        effect: updateKeyInStore(updateKey),
        predicate: (v) => v === 'CA',
        comment: true,
        dependencies: [
          {
            name: 'pleaseUploadACopyOfYourProofOfFranchiseTaxPayment',
            label:
              'Please upload a copy of your proof of Franchise Tax payment.',
            type: 'upload',
            effect: updateKeyInStore(updateKey),
            predicate: (v) => v === 'yes',

            comment: true,
          },
        ],
      },
    ],
  },
  {
    name: 'isYourBusinessRegisteredInMultipleStates',
    label: 'Is your business registered in multiple states?',
    type: 'radio',
    effect: updateKeyInStore(updateKey),
    tooltip: '',
    comment: true,
    dependencies: [
      {
        name: 'addAnotherState',
        label: 'Add another State',
        type: 'fieldArray',
        dependencies: [
          {
            name: 'state',
            label: 'State',
            type: 'state',
            effect: updateKeyInStore(updateKey),
            comment: true,
            dependencies: [
              {
                name: 'californiaSosNumber',
                label: 'California SOS Number',
                type: 'number',
                effect: updateKeyInStore(updateKey),

                comment: true,
                predicate: (v) => v === 'CA',
              },
              {
                name: 'didYouMakeYourYearCaliforniaFranchiseTaxPayment',
                label:
                  'Did you make your {year} California Franchise Tax Payment',
                type: 'radio',
                effect: updateKeyInStore(updateKey),
                predicate: (v) => v === 'CA',
                comment: true,
                dependencies: [
                  {
                    name: 'pleaseUploadACopyOfYourProofOfFranchiseTaxPayment',
                    label:
                      'Please upload a copy of your proof of Franchise Tax payment.',
                    type: 'upload',
                    effect: updateKeyInStore(updateKey),
                    predicate: (v) => v === 'yes',

                    comment: true,
                  },
                ],
              },
            ],
          },
        ],
        defaultDependency: { state: null },
        effect: (payload) => store.dispatch(updateFieldArrayKey(payload)),
        predicate: (v) => v === 'yes',
        comment: true,
      },
    ],
  },

  {
    name: 'willThisBeAFinalYearsTaxReturnForABusinessYouAreDissolving',
    label:
      "Will this be a final year's tax return for a business you are dissolving?",
    type: 'radio',
    effect: updateKeyInStore(updateKey),

    comment: true,
  },
  {
    name: 'isThisAZeroActivityReturn',
    label: 'Is this a zero activity return?',
    type: 'radio',
    effect: updateKeyInStore(updateKey),

    comment: true,
  },
  {
    name: 'isThisYourFirstYearFilingATaxReturnForBusiness',
    label: 'Is this your first year filing a tax return for business?',
    type: 'radio',
    effect: updateKeyInStore(updateKey),
    dependencies: [
      {
        name: 'pleaseUploadACopyOfYourPreviousYearsTaxReturn',
        label: "Please upload a copy of your previous year's tax return.",
        type: 'upload',
        effect: updateKeyInStore(updateKey),
        predicate: (v) => v === 'no',
      },
    ],
  },

  {
    name: 'wereYouRequiredToPayAFranchiseTaxOrAnnualFilingFeeThisTaxYear',
    label:
      'Were you required to pay a Franchise Tax or Annual Filing Fee this tax year?',
    type: 'radio',
    effect: updateKeyInStore(updateKey),
    tooltip: '',
    comment: true,
    dependencies: [
      {
        name: 'howMuchDidYouPay',
        label: 'How much did you pay?',
        type: 'number',
        effect: updateKeyInStore(updateKey),
        predicate: (v) => v === 'yes',
      },
    ],
  },

  {
    name: 'hasYourBusinessReceivedAK1FromAnotherSCorpOrPartnership',
    label:
      'Has your business received a K-1 from another S corp or partnership?',
    type: 'radio',
    effect: updateKeyInStore(updateKey),
  },
];

export const validationSchema = Yup.object().shape({
  businessName: Yup.string().nullable().required('required'),

  businessAddress: Yup.string().nullable().required('required'),
  city: Yup.string().nullable().required('required'),
  state: Yup.string().nullable().required('required'),
  zipCode: Validators.zipCode.required('required'),
  dateBusinessWasFormed: Yup.string().nullable().required('required'),
  thisEntityIsOwnedByAn: Yup.string().nullable().required('required'),
  isYourBusinessRegisteredInMultipleStates: Yup.string()
    .nullable()
    .required('required'),
  willThisBeAFinalYearsTaxReturnForABusinessYouAreDissolving: Yup.string()
    .nullable()
    .required('required'),
  isThisAZeroActivityReturn: Yup.string().nullable().required('required'),
  isThisYourFirstYearFilingATaxReturnForBusiness: Yup.string()
    .nullable()
    .required('required'),
  // bug
  addAnotherState: Yup.array().of(
    Yup.object().shape({
      pleaseUploadACopyOfYourProofOfFranchiseTaxPayment: requiredFileIf(
        ['state', 'didYouMakeYourYearCaliforniaFranchiseTaxPayment'],
        (
          state: string,
          didYouMakeYourYearCaliforniaFranchiseTaxPayment: string,
        ) =>
          state === 'CA' &&
          didYouMakeYourYearCaliforniaFranchiseTaxPayment === 'yes',
      ),
    }),
  ),
});
